import { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";

import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  ButtonBase,
  useTheme
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileArrowUp, faFile } from "@fortawesome/free-solid-svg-icons";
import { faArrowSquareDown, faFilePdf } from "@fortawesome/pro-solid-svg-icons";

const Preview = ({ previewImageStyle, image, icon }) => {
  if (image) {
    return (
      <Box
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundImage: `url(${image})`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center center",
          position: "static",
          ...previewImageStyle
        }}
      />
    );
  }

  return icon;
};

const FileUploadDropzone = ({
  fileName = "image",
  onFileUpload,
  setFile,
  previewImageStyle = {},
  icon = faFileArrowUp,
  reset,
  setReset,
  disabled
}) => {
  const { palette } = useTheme();

  const [dataSaving, setDataSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (reset) {
      setPreview(null);
      setReset(false);
    }
  }, [reset, setReset]);

  const handleFile = useCallback(
    async (file) => {
      console.log("file in handleFile:", file);
      if (onFileUpload) {
        setDataSaving(true);
        await onFileUpload(file);
        return setDataSaving(false);
      }

      const setPreviewImage = () => {
        const { type } = file;
        const isImageFile = type.startsWith("image/");

        if (isImageFile) {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          return (reader.onload = () => setPreview({ image: reader.result }));
        }

        const isPdfFile = type === "application/pdf";

        if (isPdfFile) {
          return setPreview({
            icon: <FontAwesomeIcon icon={faFilePdf} size="4x" />
          });
        }
        return setPreview({
          icon: <FontAwesomeIcon icon={faFile} size="4x" />
        });
      };

      setPreviewImage();
      return setFile(file);
    },
    [onFileUpload, setFile]
  );

  const onDrop = useCallback(
    async ([acceptedFile]) => {
      handleFile(acceptedFile);
    },
    [handleFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const inputProps = dataSaving ? {} : getInputProps();

  const onFileInput = ({ target }) => {
    const [file] = target?.files || [];
    if (!file) {
      return;
    }

    return handleFile(file);
  };

  return (
    <Box
      id="file-upload-container"
      style={{
        width: "100%",
        height: "100%",
        backgroundColor: palette.backgroundGray.main,
        position: "static"
      }}
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      {preview ? (
        <Preview previewImageStyle={previewImageStyle} {...preview} />
      ) : (
        <>
          <input
            id="file-uploader"
            type="file"
            onChange={onFileInput}
            style={{ display: "none" }}
            {...inputProps}
            disabled={dataSaving || disabled}
          />
          <ButtonBase
            id="file-upload-dropzone-button"
            {...getRootProps()}
            htmlFor="file-uploader"
            sx={{
              height: "100%",
              width: "100%",
              display: "block"
            }}
            disabled={dataSaving || disabled}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="100%"
              height="100%"
            >
              <Grid
                container
                direction="column"
                rowGap={1}
                alignItems="center"
                justifyContent="center"
              >
                {dataSaving ? (
                  <Grid item>
                    <CircularProgress />
                  </Grid>
                ) : (
                  <>
                    <Grid item>
                      <Typography
                        variant="caption"
                        align="center"
                        color="textSecondary"
                      >
                        {isDragActive
                          ? `Drop the ${fileName} here`
                          : `Upload ${fileName}`}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <FontAwesomeIcon
                        icon={isDragActive ? faArrowSquareDown : icon}
                        size="2x"
                        style={{
                          color: palette.backgroundGray.dark
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </ButtonBase>
        </>
      )}
    </Box>
  );
};
export default FileUploadDropzone;
